import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

import AuthenticationService from "service/auth/AuthenticationService";
import EventService from "service/event/EventService";
import UserService from "modules/user/service/UserService";

import { DS_V2_REFRESH } from "../drugSchedule/events";

import { vasBand, VAS_BANDS } from "../vasScale";
import { Stepper, QuickPick } from "../controls";
import LiveCallPanel from "./LiveCallPanel";
import CapturedSections, {
  SectionTabs,
  TAB_TO_SECTION,
  SECTION_TO_TAB,
} from "./CapturedSections";
import ReviewSummary from "./ReviewSummary";
import ReviewSave from "./ReviewSave";
import ReviewDone from "./ReviewDone";
import AiTranscriptService from "./AiTranscriptService";
import ModalPortal from "../common/ModalPortal";
import { CardDraftProvider } from "../common/useCardDraft";
import {
  Room,
  RoomEvent,
  createLocalAudioTrack,
} from "livekit-client";
import { emitSectionRefresh } from "./sectionRefreshEvents";
import {
  PATIENT_FALLBACK,
  SECTION_TABS,
  ACTIVE_TAB,
  COMPLAINT_ROWS,
  EDITOR_DRAFT,
} from "./data";
import AddressBookDialog from "../../components/AddressBookDialog";
import "../../styles/aiTranscript.scss";
import "../../styles/addressBook.scss";

const transcriptService = new AiTranscriptService();

/* The header tile's star — board 6858:8727 `icon/sparkle-purple`, Figma's own
   SVG verbatim: a 28x28 frame drawn 6px inside the 40px tile. Not the shared
   `Sparkle` from ../controls, which is the AI-badge glyph used across the
   sections and draws a different star. */
const EchoSparkle = ({ className }) => (
  <svg className={className} width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <path
      d="M14.832 11.126C14.8987 11.3853 15.0986 11.59 15.3564 11.6621L20.2119 13.0195L15.3564 14.377C15.1318 14.4397 14.9512 14.6029 14.8643 14.8154L14.833 14.9092L13.5 19.9854L12.167 14.9092C12.0993 14.6515 11.9001 14.4487 11.6436 14.377L6.78711 13.0195L11.6436 11.6621C11.9014 11.59 12.1013 11.3853 12.168 11.126L13.5 5.94922L14.832 11.126Z"
      fill="white"
      stroke="white"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);
EchoSparkle.propTypes = { className: PropTypes.string };

/**
 * "AI transcript review" — a large centered modal running a 6-state machine
 * (S0 list → S5 confirmation), all states sharing one shell. Source: Figma spec
 * board 2495:3059 and states 2618:3498 (S0) / 2526:3417 (S1) / 2526:12196 (S2) /
 * 2495:3060 (S3) / 2495:3487 (S4) / 2499:3123 (S5). Launched from the sidebar
 * "AI transcript" quick-access row (2428:5277).
 *
 * The middle 832w table and right 340w editor are the existing patient-section
 * archetype: they REUSE the global sec-card / sec-editor classes and the shared
 * controls (Stepper, QuickPick, VAS bands). New here is the modal shell, the 260w
 * live-call rail, the tab strip, the footer, and the confirmation dialog.
 *
 * UI-only: every transition is local state; there is no telephony or API. Renders
 * nothing when `open` is false.
 */

/**
 * Purple in-call consent banner. Sits atop the middle column from the
 * moment the patient joins until Consent captured / Decline is clicked.
 *
 * The server-side /captured endpoint refuses AI extractions until
 * Transcript.ConsentAt is set, so the Python agent's proposals silently
 * drop on the floor while this banner is visible — visible or not, the
 * gate is authoritative on the backend.
 *
 * Decline hangs up the call in addition to marking ConsentDeclined; the
 * banner is a hard fork, not a nudge.
 */
const ConsentBanner = ({ status, error, onCapture, onDecline }) => {
  const busy = status === "busy";
  return (
    <div className="ait-consent-banner" role="region" aria-label="Consent capture">
      <div className="ait-consent-copy">
        <p className="ait-consent-title">Read the consent statement to the patient</p>
        <p className="ait-consent-sub">
          Confirm they agree to be recorded and transcribed. Click Consent
          captured once they’ve said yes — the AI won’t start capturing
          items until then.
        </p>
        {error ? <p className="ait-consent-err">{error}</p> : null}
      </div>
      <div className="ait-consent-actions">
        <button
          type="button"
          className="ait-consent-primary"
          onClick={onCapture}
          disabled={busy}
        >
          {busy ? "Saving…" : "Consent captured"}
        </button>
        <button
          type="button"
          className="ait-consent-link"
          onClick={onDecline}
          disabled={busy}
        >
          Decline & end call
        </button>
      </div>
    </div>
  );
};
ConsentBanner.propTypes = {
  status:    PropTypes.oneOf(["unset", "busy", "captured", "declined"]).isRequired,
  error:     PropTypes.string,
  onCapture: PropTypes.func.isRequired,
  onDecline: PropTypes.func.isRequired,
};
ConsentBanner.defaultProps = { error: "" };

/** VAS severity chip for the embedded table (reuses the global .vas-chip). */
const VasChip = ({ score }) => {
  const band = vasBand(score);
  return (
    <button
      type="button"
      className="vas-chip"
      style={{ background: band.bg, color: band.fg }}
      title="Editing severity isn't wired up yet"
    >
      <span className="vas-chip-label">{band.label}</span>
      <span className="vas-chip-caret" aria-hidden="true">
        &#9662;
      </span>
    </button>
  );
};
VasChip.propTypes = { score: PropTypes.number.isRequired };

/** Middle column: horizontal tab strip + the Chief-complaints sec-card table. */
const SectionsArea = ({ onAddComplaint }) => (
  <div className="ait-sections">
    <div className="ait-tabs" role="tablist">
      {SECTION_TABS.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={tab.id === ACTIVE_TAB}
          className={`ait-tab${tab.id === ACTIVE_TAB ? " is-active" : ""}`}
        >
          {tab.label}
          {tab.count != null ? <span className="ait-tab-count">{tab.count}</span> : null}
        </button>
      ))}
    </div>

    <div className="ait-table-scroll">
    <div className="sec-card">
      <header className="sec-card-head">
        <h2 className="sec-card-title">Chief complaints</h2>
        <span className="sec-card-count">{COMPLAINT_ROWS.length} entries</span>
        <button type="button" className="sec-add-btn" onClick={onAddComplaint}>
          <span className="sec-add-plus">+</span> Add complaints
        </button>
      </header>

      <div className="sec-table cc-grid" role="table">
        <div className="sec-thead" role="row">
          <span role="columnheader">Complaint</span>
          <span role="columnheader">Since when</span>
          <span role="columnheader">Description</span>
          <span role="columnheader">Status</span>
          <span role="columnheader">Analysis</span>
        </div>

        {COMPLAINT_ROWS.map((c) => (
          <div className="sec-row" role="row" key={c.id}>
            <span className="sec-cell cc-name" role="cell">
              {c.complaint}
            </span>
            <span className="sec-cell cc-since" role="cell">
              {c.since}
            </span>
            <span className="sec-cell cc-desc" role="cell">
              {c.description}
            </span>
            <span className="sec-cell" role="cell">
              <VasChip score={c.score} />
            </span>
            <span className="sec-cell cc-analysis" role="cell">
              {c.hasTrend ? (
                <button type="button" className="cc-trend" title="Trend view isn't wired up yet">
                  View trend
                </button>
              ) : (
                <span className="cc-dash">&mdash;</span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
    </div>
  </div>
);
SectionsArea.propTypes = { onAddComplaint: PropTypes.func.isRequired };

/**
 * S3/S4 right editor — the sec-editor archetype with a maroon "reviewing" accent.
 * Reuses Stepper + QuickPick and the global sec-* form classes; local state only.
 */
const ReviewEditor = ({ onAdd, onDelete }) => {
  const [query, setQuery] = useState(EDITOR_DRAFT.query);
  const [vas, setVas] = useState(EDITOR_DRAFT.vas);
  const [years, setYears] = useState(EDITOR_DRAFT.years);
  const [months, setMonths] = useState(EDITOR_DRAFT.months);
  const [days, setDays] = useState(EDITOR_DRAFT.days);
  const [description, setDescription] = useState(EDITOR_DRAFT.description);

  const vasLabel = (VAS_BANDS.find((b) => b.key === vas) || VAS_BANDS[1]).label;

  return (
    <div className="sec-editor ait-reviewing">
      <header className="sec-editor-head">
        <span className="sec-editor-title">Complaint</span>
        <span className="sec-editor-sub">ICD-enabled · fuzzy search</span>
        <span className="ait-code">{EDITOR_DRAFT.code}</span>
      </header>

      <div className="sec-field">
        <div className="sec-search">
          <svg className="sec-search-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
            <circle cx="9" cy="9" r="6" />
            <path d="M14 14l4 4" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search a complaint"
          />
        </div>
      </div>

      <QuickPick
        label="Diagnosis"
        suggestedFor={EDITOR_DRAFT.suggestedFor}
        items={EDITOR_DRAFT.suggestedItems}
        extra={EDITOR_DRAFT.suggestedExtra}
      />

      <div className="sec-field">
        <label className="sec-label">
          Status <span className="sec-label-note">· VAS scale</span>
        </label>
        {/* Inert VAS dropdown — cycles the band on click; menu isn't wired. */}
        <button
          type="button"
          className="ait-select"
          onClick={() => {
            const i = VAS_BANDS.findIndex((b) => b.key === vas);
            setVas(VAS_BANDS[(i + 1) % VAS_BANDS.length].key);
          }}
        >
          <span>{vasLabel}</span>
          <span className="ait-select-caret" aria-hidden="true">
            &#9662;
          </span>
        </button>
      </div>

      <div className="sec-field">
        <label className="sec-label">Duration</label>
        <div className="cc-duration">
          <Stepper label="Years" value={years} onChange={setYears} max={120} />
          <Stepper label="Months" value={months} onChange={setMonths} max={11} />
          <Stepper label="Days" value={days} onChange={setDays} max={31} />
        </div>
        <p className="cc-autocalc">
          Auto-calc · Since when: <strong>{EDITOR_DRAFT.sinceWhen}</strong>
        </p>
      </div>

      <div className="sec-field">
        <label className="sec-label" htmlFor="ait-desc">
          Description <span className="sec-label-note">optional</span>
        </label>
        <textarea
          id="ait-desc"
          className="sec-textarea"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className="sec-editor-actions">
        <button type="button" className="sec-btn sec-btn-ghost" onClick={onDelete}>
          Delete
        </button>
        <button type="button" className="sec-btn sec-btn-primary" onClick={onAdd}>
          + Add
        </button>
      </div>
    </div>
  );
};
ReviewEditor.propTypes = {
  onAdd: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

/**
 * Header status pill per step.
 *   0: idle (no pill)
 *   1: waiting for patient
 *   2: live call
 *   3: post-call (call ended, waiting for review to start)
 *   4: review summary dialog on top
 *   5: review & save (own header — no pill)
 *   6: done confirmation dialog on top
 */
const PILLS = {
  1: { label: "CALLING…", cls: "is-calling" },
  2: { label: "LIVE", cls: "is-live" },
  3: { label: "CALL ENDED", cls: "is-ended" },
};

const AiTranscriptModal = ({
  open, onClose, patient, appointment,
  autoCallTarget, onAutoCallConsumed,
  onCallActiveChange,
  seededSession,
  onCallRequest,
  /* Broadcast hooks used by ActiveCallProvider — the modal is now
     mounted at app root and its internal state (session, callState,
     step, initError) has to flow up so the floater can display live
     info and the provider can decide minimise vs dismiss. Safe no-ops
     when a legacy caller mounts the modal directly without a provider. */
  onSessionChange,
  onCallStateChange,
  onStepChange,
  onInitErrorChange,
  registerHandle,
}) => {
  const [step, setStep] = useState(0);
  // "Nothing to review" confirmation dialog visibility. Previously the
  // step-4 ReviewSummary surfaced an inline Finish button for empty
  // transcripts; since step 4 is now skipped, we open this little
  // prompt from the Review-call click when every captured item is
  // rejected/deleted. OK → saveSection + close; Cancel → stay on the
  // ended-call screen so the reviewer can revisit a section.
  const [nothingToReviewOpen, setNothingToReviewOpen] = useState(false);

  // ZY-10356 Phase 1 — latching flag that stays true once step first
  // reaches 2+. Keeps CapturedSections mounted (hidden via CSS) across
  // back-nav to the list view (step 0), so per-section open-card
  // arrays and the forms inside survive the trip.
  const [capturedEverMounted, setCapturedEverMounted] = useState(false);
  useEffect(() => {
    if (step > 1 && !capturedEverMounted) setCapturedEverMounted(true);
  }, [step, capturedEverMounted]);

  // ZY-10356 Phase 1 — per-transcript open-card store. Modal-root ref
  // keyed by transcriptId; each CapturedSections instance (keyed on
  // transcriptId below) hydrates from here on mount and writes back
  // on unmount, so switching to a past transcript and returning to
  // the live one restores the open cards the reviewer had typed into.
  // Within a single transcript, state preservation across back-nav /
  // minimize is handled by capturedEverMounted keeping the instance
  // alive. Across transcripts, the store survives the unmount cycle.
  const openCardsStoreRef = useRef(new Map());
  /* Tell the owner whether a call is live — ringing (1) or connected (2), the
     same window this modal's own gates use (`inCall` below). Reported from HERE,
     above the `if (!open) return null` guard, because the modal stays mounted
     while closed and a call keeps running behind it: the poll still moves it to
     step 3 when either side hangs up. The patient screen holds "Update booking
     for" while this is true. */
  const liveCall = step === 1 || step === 2;
  const onCallActiveChangeRef = useRef(onCallActiveChange);
  onCallActiveChangeRef.current = onCallActiveChange;
  useEffect(() => {
    if (onCallActiveChangeRef.current) onCallActiveChangeRef.current(liveCall);
  }, [liveCall]);
  useEffect(() => () => {
    if (onCallActiveChangeRef.current) onCallActiveChangeRef.current(false);
  }, []);

  /* State broadcast to ActiveCallProvider. Refs let us fire the current
     callbacks without re-running the effect when the parent recreates
     them. Each broadcast fires whenever the corresponding local state
     moves — the provider mirrors these into activeCall for the floater
     and its minimise/dismiss decisions. Legacy mounts without a provider
     leave these props undefined; the guards below make every fire a
     no-op there. */
  const onStepChangeRef = useRef(onStepChange);
  onStepChangeRef.current = onStepChange;
  useEffect(() => {
    if (onStepChangeRef.current) onStepChangeRef.current(step);
  }, [step]);

  const onCallStateChangeRef = useRef(onCallStateChange);
  onCallStateChangeRef.current = onCallStateChange;

  const onSessionChangeRef = useRef(onSessionChange);
  onSessionChangeRef.current = onSessionChange;

  const onInitErrorChangeRef = useRef(onInitErrorChange);
  onInitErrorChangeRef.current = onInitErrorChange;
  const [transcripts, setTranscripts] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Past-call recording playback (Phase 3). `player` carries the state
  // for the floating audio bar: {url, contentType, transcriptId, label}
  // when active, null when hidden. Loading is tracked separately so the
  // rail chip can show a spinner while the signed URL is being fetched.
  const [player, setPlayer] = useState(null);
  const [playerLoading, setPlayerLoading] = useState(false);
  const [playerError, setPlayerError] = useState("");
  const handlePlayRecording = useCallback((t) => {
    const rid = t && t.callRecording && t.callRecording.recordingId;
    if (!rid) return;
    setPlayerError("");
    setPlayerLoading(true);
    transcriptService.getRecordingDownloadUrl(
      rid,
      (resp) => {
        setPlayerLoading(false);
        if (!resp || !resp.url) {
          setPlayerError("Recording is not available.");
          return;
        }
        setPlayer({
          url: resp.url,
          contentType: resp.contentType || "audio/ogg",
          transcriptId: t.id,
          label: (t.startedAt || "").slice(0, 10),
        });
      },
      (err) => {
        setPlayerLoading(false);
        setPlayerError((err && err.message) || "Could not load recording.");
      },
    );
  }, []);
  const closePlayer = useCallback(() => {
    setPlayer(null);
    setPlayerError("");
  }, []);

  // Live-call session — populated after a successful /initiate + LiveKit
  // connect. Cleared on hang-up / modal close. The cleanup fn tears down
  // the LiveKit Room and detaches every attached <audio> element.
  const [session, setSession] = useState(null);
  const [initiating, setInitiating] = useState(false);
  const [initError, setInitError] = useState("");
  const sessionRef = useRef(null);
  useEffect(() => { sessionRef.current = session; }, [session]);
  useEffect(() => {
    if (onSessionChangeRef.current) onSessionChangeRef.current(session);
  }, [session]);
  useEffect(() => {
    if (onInitErrorChangeRef.current) onInitErrorChangeRef.current(initError);
  }, [initError]);

  // Address-book dialog local state. Opened from the header icon (steps
  // 0-3, disabled during a live call — matches the icon's own gate) and
  // from a Call button inside the dialog: the dialog closes, we call
  // startCall(override) with the picked number so /initiate targets it
  // instead of the default primary.
  const [addressBookOpen, setAddressBookOpen] = useState(false);

  // Person + patientId derived here so the effects below can reference
  // them without a use-before-define lint warning.
  const person = patient || appointment || PATIENT_FALLBACK;
  const patientId = person.id;

  // Live-turns state. Populated by a 2s poll of GET /transcripts/:pid/:tid
  // while a call is active. Kept as a stable array (server-owned) so the
  // rail renders the authoritative history on browser refresh too.
  //
  // IMPORTANT: this effect depends only on the transcriptId string, NOT
  // the whole session object. Depending on `session` and mutating it
  // inside the tick (to sync version) tore down + re-created the
  // interval on every response — the runaway that produced 7k requests
  // in a few seconds. The transcriptId is stable for the life of a call,
  // so the interval runs exactly once per call. Version syncing happens
  // via functional setSession that early-returns on unchanged version.
  const [liveTurns, setLiveTurns] = useState([]);
  const [capturedItems, setCapturedItems] = useState([]);
  // Live captions from the agent over the LK data channel (topic
  // "caption"): segId → { id, speaker, text, final, at }. Display-only
  // overlay for the live rail — interims render grey until the 2s poll
  // brings the committed turn (turn.id === segId), then the overlay entry
  // is dropped. Never persisted, never used for review/save. If LK data
  // doesn't arrive (agent publisher down) the rail simply behaves as before.
  const [captions, setCaptions] = useState({});
  const CAPTION_TTL_MS = 30000;
  // A caption is "covered" once the poll has its committed turn: same id
  // (agent seg ids are ObjectIds == turn.id), or — for agents that still
  // send non-ObjectId seg ids — a final caption with the same speaker+text.
  const isCaptionCovered = (c, ids, texts) =>
    ids.has(c.id) || (c.final && texts.has(`${c.speaker}|${c.text.trim()}`));
  useEffect(() => {
    setCaptions((prev) => {
      const keys = Object.keys(prev);
      if (keys.length === 0) return prev;
      const ids = new Set(liveTurns.map((t) => t.id));
      const texts = new Set(liveTurns.map((t) => `${t.speaker}|${(t.text || "").trim()}`));
      const now = Date.now();
      const next = {};
      let changed = false;
      keys.forEach((k) => {
        const c = prev[k];
        if (isCaptionCovered(c, ids, texts) || now - c.at > CAPTION_TTL_MS) changed = true;
        else next[k] = c;
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveTurns]);
  // Live rail = committed turns + not-yet-committed captions (newest last).
  // Only the live rail gets this; review/save keep using liveTurns.
  const railTurns = useMemo(() => {
    const pending = Object.values(captions);
    if (pending.length === 0) return liveTurns;
    const ids = new Set(liveTurns.map((t) => t.id));
    const texts = new Set(liveTurns.map((t) => `${t.speaker}|${(t.text || "").trim()}`));
    const now = Date.now();
    const extra = pending
      .filter((c) => !isCaptionCovered(c, ids, texts) && now - c.at <= CAPTION_TTL_MS)
      .sort((a, b) => a.at - b.at)
      .map((c) => ({ id: c.id, speaker: c.speaker, text: c.text, final: false, interim: true }));
    return extra.length ? liveTurns.concat(extra) : liveTurns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveTurns, captions]);
  // ZY-10356 Phase 4 — map of section → { reviewedAt, reviewedBy }
  // from the transcript doc. Hydrated by the poll + mutation responses.
  // Review step 5 reads this to flip the per-section footer between
  // Save & next (unreviewed) and Already-reviewed chip + Go to next.
  const [reviewedSections, setReviewedSections] = useState({});
  const [activeSection, setActiveSection] = useState("patient-details");
  // Per-section total of open cards (AI proposals + manual/update/re-edit
  // editors currently on screen). CapturedSections owns this state and
  // pushes it up on every change via onOpenCountsChange. The modal reads
  // it to (a) show a "● Label N" dot on each tab and (b) gate the
  // Review call button on all sections being empty after call-end.
  const [openCountsBySection, setOpenCountsBySection] = useState({});
  // Server-side call state, driven by LK-SIP participant events on the
  // Go side. Poll pushes updates into this so the S1 rail can flip
  // between "Ringing care manager…" / "…patient…" / "Connected" and the
  // modal can auto-transition step 1→2 (both parties on line) or
  // step 1|2→3 (either party hangs up).
  //   ""                 idle / no active call
  //   "waiting_cm"       browser-mode aspire: token minted, CM hasn't joined LK yet
  //   "ringing_cm"       legacy SIP-mode: Go SIP-dialled the care manager
  //   "ringing_patient"  patient SIP INVITE placed
  //   "connected"        patient joined the LK room
  //   "dropped"          either party disconnected — Go will tear the other leg down
  const [callState, setCallState] = useState("");
  /* Broadcast callState / session / initError to ActiveCallProvider on
     every change. Refs are declared up top so the effects here don't
     re-fire when the parent recreates the callback. */
  useEffect(() => {
    if (onCallStateChangeRef.current) onCallStateChangeRef.current(callState);
  }, [callState]);
  // Highest transcript version we've applied from ANY source (poll,
  // decide, propose, consent, saveSection). Ref so both poll callbacks
  // and mutation callbacks read/write the latest without waiting for a
  // React re-render.
  //
  // Race problem this closes: user clicks Save in a review card at
  // T+0. decide POST is in-flight when a scheduled poll tick fires at
  // T+50ms. Server processes them concurrently; if the poll's GET
  // completes its Mongo read BEFORE decide's write commits, the poll
  // response echoes the OLD payload (with version N). Decide response
  // returns version N+1 with the new payload — but if it lands FIRST
  // and applyMutation sets state to N+1, the poll's older N response
  // then clobbers state back to N. Table shows stale values, card on
  // re-open shows fresh (poll only overwrites this state; card reads
  // straight from the fresh item after applyMutation).
  //
  // Fix: version-gate the poll. Any response whose version is strictly
  // less than what we've already applied is dropped.
  const latestVersionRef = useRef(0);
  const sessionTranscriptId = session ? session.transcriptId : null;
  useEffect(() => {
    // Reset the ref when a call starts so the next session's poll
    // doesn't inherit a stale watermark.
    latestVersionRef.current = 0;
    // ZY-10356 — refetch the transcript list whenever a NEW session
    // appears so the fresh in-progress transcript is included. The
    // list is otherwise loaded once on open (and on specific hooks
    // below), so a Call-patient click followed by an immediate back
    // nav would otherwise land on a stale list that doesn't show the
    // just-started LIVE row.
    if (sessionTranscriptId) refetchTranscripts();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [sessionTranscriptId]);
  const applyIfFresh = (doc) => {
    if (!doc) return;
    const v = typeof doc.version === "number" ? doc.version : 0;
    if (v < latestVersionRef.current) return; // stale
    latestVersionRef.current = v;
    if (Array.isArray(doc.turns)) {
      setLiveTurns(doc.turns.filter((t) => t.final));
    }
    if (Array.isArray(doc.captured)) {
      setCapturedItems(doc.captured);
    }
    // ZY-10356 Phase 6 — ALWAYS reset reviewedSections from the fresh
    // doc, defaulting to `{}` when the server omits the key (brand-new
    // transcript, or `omitempty` elided). Earlier version only updated
    // on a truthy value, which left stale state from a prior-transcript
    // session showing the lockout banner on a fresh call.
    setReviewedSections((doc.reviewedSections && typeof doc.reviewedSections === "object")
      ? doc.reviewedSections
      : {});
    if (typeof doc.callState === "string") {
      setCallState(doc.callState);
    }
    setSession((prev) => {
      if (!prev || prev.version === v) return prev;
      return { ...prev, version: v };
    });
  };
  const transcriptIdForPoll = session ? session.transcriptId : null;
  useEffect(() => {
    // Two shapes in one effect:
    //   S0 (idle, no session)  → nothing to fetch; wipe liveTurns.
    //   S0 (back-nav, session) → tick every 2s so a patient-side hang-up
    //                            (ZY-10356) is observed and the dropped
    //                            useEffect auto-advances to step 3.
    //   S1 (ringing) / S2      → tick every 2s. Server-pushed callState
    //   (connected)              drives the step transitions.
    //   S3-S5 (review)         → tick ONCE and stop. Populates liveTurns
    //                            for past-transcript view (click-through
    //                            from S0 list) without wasting cycles on
    //                            a 2s ticker after the call ends.
    //                            2026-09-28 gated the poll off for
    //                            step>2 to stop the "polls forever" bug
    //                            but that also killed the one-shot
    //                            fetch reviewers depend on — 2026-09-30
    //                            restored it as a single tick.
    //
    // Session presence (transcriptIdForPoll) is the gate, not step. The
    // old `step < 1` guard dropped polling on the back-nav list view
    // and the modal never observed the patient hang-up server-side.
    if (!transcriptIdForPoll || !patientId) {
      setLiveTurns([]);
      return undefined;
    }
    let alive = true;
    const tick = () => {
      transcriptService.get(
        patientId, transcriptIdForPoll,
        (doc) => {
          if (!alive) return;
          applyIfFresh(doc);
        },
        () => { /* transient — next tick retries */ },
      );
    };
    tick();
    // Review (step 3-5): one-shot only. No interval.
    if (step > 2) return () => { alive = false; };
    // Live call (S1/S2): keep ticking so callState updates land.
    const id = setInterval(tick, 2000);
    return () => { alive = false; clearInterval(id); };
  }, [transcriptIdForPoll, patientId, step]);

  // Auto-transition step from callState pushed by the poll.
  //   ringing_cm / ringing_patient  →  stay on step 1 (rail copy changes)
  //   connected                     →  step 1 → step 2 (live view)
  //   dropped @ step 1 (unanswered) →  step 0 (transcripts list) — the
  //                                    call never connected, so there's
  //                                    nothing to review. Full teardown
  //                                    via endSession() so the reopen
  //                                    won't reuse a dead transcriptId.
  //   dropped @ step 2 (hung up)    →  step 3 (post-call review) — a
  //                                    live conversation happened, its
  //                                    captures deserve a review pass.
  // The `Skip wait` affordance previously in the S1 rail is gone —
  // the server drives the transition now.
  // ZY-10356 — the "dropped" transitions below are EDGE-triggered: they
  // only fire when callState moves from a live value to "dropped", not
  // every time the effect re-runs with (step=X, callState="dropped")
  // already paired. Otherwise a back-nav from step 3 → step 0 would
  // re-observe (callState=dropped, step=0) and ping-pong straight back
  // to step 3, trapping the reviewer out of the list view.
  const prevCallStateRef = useRef("");
  useEffect(() => {
    const prev = prevCallStateRef.current;
    prevCallStateRef.current = callState;
    const justEnded = callState === "dropped" && !!prev && prev !== "dropped";

    if (callState === "connected" && step === 1) {
      setStep(2);
    } else if (justEnded && step === 1) {
      // endSession posts /end (server flips status in-progress →
      // pending). Refetch AFTER it resolves so the S0 list shows the
      // just-cancelled transcript as PENDING instead of stale LIVE.
      endSession().then(() => refetchTranscripts());
      setStep(0);
    } else if (justEnded && step === 2) {
      // ZY-10312 — optimistic flip of the active row's badge to
      // PENDING so the S0 list never shows a stale LIVE in the gap
      // between /end firing and refetch landing. Then await
      // pauseSession's /end before the refetch so the server-side
      // status flip is persisted before we re-read the list — a bare
      // refetch would race the /end and still pull LIVE.
      flipActiveTranscriptToPending();
      pauseSession().then(() => refetchTranscripts());
      setCallEndedAt(new Date().toISOString());
      setStep(3);
    } else if (justEnded && step === 0) {
      // Reviewer back-navigated to the transcript list while the call
      // was still live, then the call ended (patient hung up, agent
      // dropped, etc.). Mirror the step===2 branch so the modal
      // advances to the ended-call main view (step 3), matching how
      // the floater's restore would land. Only fires on the actual
      // end edge — back from step 3 won't re-trigger because
      // justEnded is false while callState has been dropped already.
      flipActiveTranscriptToPending();
      pauseSession().then(() => refetchTranscripts());
      setCallEndedAt(new Date().toISOString());
      setStep(3);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [callState, step]);

  // Consent tri-state, tracks the server-side ConsentAt / ConsentDeclined
  // fields for the active call:
  //   "unset"     — banner shown; agent /captured posts are blocked server-side
  //   "captured"  — banner hidden; extraction unblocked
  //   "declined"  — banner hidden; call ends immediately after this posts
  //   "busy"      — POST in flight; both banner buttons disabled
  //
  // Product change: explicit consent is temporarily NOT required. The
  // agent now plays a "This call will be recorded" TTS notice at
  // patient join instead. Initial state (and the fresh-call reset
  // below) is hardcoded to "captured" so the banner never renders and
  // downstream gating stays off. The postConsent helper + the /consent
  // endpoint are kept dormant so re-enabling is a one-line flip back
  // to useState("unset") + restoring the reset.
  const [consentStatus, setConsentStatus] = useState("captured");
  const [consentError, setConsentError] = useState("");
  useEffect(() => {
    if (!session) {
      setConsentStatus("captured");
      setConsentError("");
    }
  }, [session]);

  // Accept / discard of AI-captured items happens in the post-call
  // review flow now, not during the call. `decideCaptured` will be
  // reintroduced there.

  // Track when the call actually started so the review screen can show
  // the correct timestamp + duration.
  const [callStartedAt, setCallStartedAt] = useState("");
  const [callEndedAt,   setCallEndedAt]   = useState("");
  // Status of the currently-open transcript ("in-progress" | "pending"
  // | "reviewed" | ""). Set when the user reopens a past transcript
  // from the S0 list; drives the "hide Review call once reviewed" +
  // "hide address book while viewing an existing transcript" gates
  // in the header. A fresh outgoing call has no status yet ("").
  const [activeTranscriptStatus, setActiveTranscriptStatus] = useState("");
  // Per-section counts of items that reached `reviewed_and_saved` —
  // populated as the reviewer clicks Save & next through each section.
  // Feeds the final ReviewDone dialog.
  const [savedCounts, setSavedCounts] = useState({});

  // Called by ReviewSave when the reviewer clicks Save & next on the
  // active section. POSTs /section/:section/save (Phase 1 stub: flips
  // per-item state to reviewed_and_saved on the transcript; the actual
  // fan-out to CC v2 / Dx v2 / Med v2 lands in Phase 4).
  const saveSection = (section, skipOnFailure = false) => new Promise((resolve, reject) => {
    const s = sessionRef.current;
    if (!s || !patientId) { reject(new Error("Session lost — reopen the modal.")); return; }
    // `latestVersionRef.current` is bumped SYNCHRONOUSLY inside every
    // mutation response, so it reflects the most recent server-
    // acknowledged version even before React finishes re-rendering.
    //
    // No client-side 409 retry: api-ai-transcript-v2-service's
    // SaveSection now wraps its final doc write in saveWithRetry (Go
    // side), so a background AI emission that bumps the version while
    // the reviewer is on the Review screen no longer surfaces 409 to
    // the browser. If the backend's bounded loop exhausts (very rare),
    // the error propagates normally and the reviewer can retry.
    const expectedVersion = latestVersionRef.current || s.version || 0;
    transcriptService.saveSection(
      patientId, s.transcriptId, section,
      { skipOnFailure, expectedVersion },
      (resp) => {
        if (resp && resp.transcript) {
          if (Array.isArray(resp.transcript.captured)) {
            setCapturedItems(resp.transcript.captured);
          }
          if (typeof resp.transcript.version === "number") {
            if (resp.transcript.version > latestVersionRef.current) {
              latestVersionRef.current = resp.transcript.version;
            }
            setSession((prev) => (prev ? { ...prev, version: resp.transcript.version } : prev));
          }
          // ZY-10378 — mutation response carries the fresh reviewedSections
          // map after Save & next stamps the current section. Sync locally
          // so the tab strip ✓ + green name + "already reviewed" footer
          // flip immediately rather than waiting for the next 2s poll tick.
          // `omitempty` on the server side means the key is absent when
          // empty, so default to `{}`.
          setReviewedSections(
            (resp.transcript.reviewedSections && typeof resp.transcript.reviewedSections === "object")
              ? resp.transcript.reviewedSections
              : {},
          );
        }
        // Count only items that actually landed in reviewed_and_saved for
        // the ReviewDone breakdown — skipped-after-failure items don't
        // count as "saved to CC v2".
        const savedNow = Array.isArray(resp && resp.results)
          ? resp.results.filter((r) => r && r.success).length
          : 0;
        if (savedNow > 0) {
          setSavedCounts((prev) => ({
            ...prev,
            [section]: (prev[section] || 0) + savedNow,
          }));
          // Nudge the standalone section to re-fetch so the patient
          // page reflects the just-saved items the instant the review
          // modal closes. Without this, the reviewer has to hit
          // browser refresh to see what they just approved.
          emitSectionRefresh(section);
        }
        resolve(resp);
      },
      (err) => reject(new Error((err && err.message) || "Save failed")),
    );
  });

  const postConsent = (captured) => {
    const s = sessionRef.current;
    if (!s || !patientId) return;
    setConsentError("");
    setConsentStatus("busy");
    transcriptService.consent(
      patientId, s.transcriptId,
      { captured, expectedVersion: s.version || 0 },
      (resp) => {
        // Server echoes the fresh Transcript; bump our tracked version so
        // subsequent mutations don't 409.
        const v = resp && resp.transcript && resp.transcript.version;
        setSession((prev) => (prev ? { ...prev, version: v || prev.version } : prev));
        if (captured) {
          setConsentStatus("captured");
        } else {
          setConsentStatus("declined");
          // Decline ends the call. Tear down LiveKit + close the modal so
          // the caller can't accidentally keep recording.
          endSession().then(() => onClose && onClose());
        }
      },
      (err) => {
        setConsentStatus("unset");
        setConsentError((err && err.message) || "Could not save consent.");
      },
    );
  };

  // Fetch the S0 transcript list. Extracted so it can also be invoked
  // when returning to step 0 mid-modal (e.g. an unanswered call →
  // dropped-at-step-1 falls back to the list, and the just-ended
  // transcript needs to be re-read so its status flips from LIVE to
  // PENDING). Falsy appointmentId falls back to the patient-wide list.
  // Optimistic update for the end-call flow: before the /end round-trip
  // resolves and the refetch catches up, flip the active transcript's
  // row in local state so the S0 list badge reads PENDING instead of
  // the stale LIVE from the initial fetch. Idempotent — a no-op if the
  // row is already past in-progress or the active session isn't known.
  // Called from endCall and both dropped-callState branches that end a
  // live session.
  const flipActiveTranscriptToPending = useCallback(() => {
    const s = sessionRef.current;
    const activeId = s && s.transcriptId;
    if (!activeId) return;
    const nowIso = new Date().toISOString();
    setTranscripts((prev) =>
      prev.map((t) =>
        t && t.id === activeId && t.status === "in-progress"
          ? { ...t, status: "pending", endedAt: t.endedAt || nowIso }
          : t
      )
    );
  }, []);

  const refetchTranscripts = useCallback(() => {
    if (!patientId) {
      setTranscripts([]);
      setLoadError(false);
      setLoadingList(false);
      return;
    }
    setLoadingList(true);
    setLoadError(false);
    transcriptService.list(
      patientId,
      appointment && appointment.id,
      (resp) => {
        setTranscripts(Array.isArray(resp && resp.transcripts) ? resp.transcripts : []);
        setLoadingList(false);
      },
      () => {
        setTranscripts([]);
        setLoadError(true);
        setLoadingList(false);
      },
    );
  }, [patientId, appointment, transcriptService]);

  /* Reset + refetch each time the modal is genuinely (re)opened for a
     FRESH session. Under ActiveCallProvider the modal stays mounted
     while minimised — restoring from the floater flips `open` back to
     true, and this effect would otherwise slam step back to 0 (the
     transcripts list) even though a call is still live behind it.
     `sessionRef.current` is the live-call flag: session gets set when a
     call is placed and cleared by endSession, so guarding on it skips
     the reset for the restore path (session present) and keeps the
     original behaviour for genuine fresh opens (session absent). */
  useEffect(() => {
    if (!open) return;
    if (sessionRef.current) return;
    setStep(0);
    refetchTranscripts();
  }, [open, refetchTranscripts]);

  // Two levels of teardown:
  //
  //   pauseSession()  — the call is over but the modal stays open for the
  //                     review flow. POST /end tells Go to hang up the
  //                     patient SIP leg + close the LK room; on the
  //                     browser side we also disconnect from LiveKit.
  //                     `session` is KEPT so the review still has
  //                     transcriptId + patientId to poll.
  //
  //   endSession()    — full teardown on modal close / cancel. Pauses +
  //                     nulls session so the poll effect stops.
  //
  // Browser-mode (aspire): the CM joins the LK room in-browser via
  // WebRTC and holds a live Room instance for the call's duration; the
  // patient joins via SIP. Legacy SIP-mode carries both parties on
  // phones and never held a browser-side LK connection — pauseSession
  // is a no-op on the LK side when roomRef is empty.
  const roomRef = useRef(null);
  const localMicRef = useRef(null);
  const remoteAudiosRef = useRef(new Map()); // trackSid → <audio> element
  // Guard against joinRoomAndDial firing twice for the same transcript.
  // Two entry points call it (the startCall success handler and the
  // seededSession effect), and React 18 strict-mode + dev double-invokes
  // effects — either can double-fire within ~20ms and land a duplicate
  // /dial-patient. Tracking the last transcript we've committed to
  // joining lets a second call for the same id short-circuit cleanly
  // while still allowing a genuinely new transcript to open its room.
  // Reset in disconnectRoom() so a next call can start fresh.
  const joinedTranscriptRef = useRef(null);

  // Tear down every subscribed remote audio + our local mic + the room.
  // Safe to call multiple times; sets refs back to null.
  const disconnectRoom = () => {
    try {
      remoteAudiosRef.current.forEach((el) => {
        try { el.remove(); } catch (_) { /* noop */ }
      });
      remoteAudiosRef.current.clear();
      if (localMicRef.current) {
        try { localMicRef.current.stop(); } catch (_) { /* noop */ }
        localMicRef.current = null;
      }
      if (roomRef.current) {
        try { roomRef.current.disconnect(); } catch (_) { /* noop */ }
        roomRef.current = null;
      }
      joinedTranscriptRef.current = null;
      setCaptions({});
    } catch (_) { /* noop */ }
  };

  const pauseSession = () => {
    disconnectRoom();
    const s = sessionRef.current;
    if (!s || !s.transcriptId || !patientId) return Promise.resolve();
    // Await the /end round-trip — callers (endSession → refetchTranscripts
    // after a dropped call) rely on the server having flipped status
    // in-progress → pending before the list is re-read, so the S0 rail
    // doesn't show the just-cancelled call as a stale LIVE.
    return new Promise((resolve) => {
      transcriptService.end(
        patientId, s.transcriptId,
        { expectedVersion: s.version || 0 },
        () => resolve(),
        () => resolve(),
      );
    });
  };

  // Browser-mode: connect to LK, publish mic, then trigger the patient
  // SIP INVITE. `livekit` carries {url, room, token} from /initiate (or
  // from the aspire seed). On failure of ANY step, we clean up the LK
  // side and surface `initError` so the header can show it; the server-
  // side transcript stays in waiting_cm until it times out or the
  // reviewer cancels.
  const joinRoomAndDial = async ({
    patientId: pid, transcriptId, livekit,
  }) => {
    if (!livekit || !livekit.url || !livekit.token) {
      setInitError("LiveKit session missing — can't set up the call.");
      return;
    }
    // Idempotency guard — if this same transcript is already in-flight
    // (room connected, mic published) or has been kicked off by a
    // sibling code path, don't fire again. Second call would produce a
    // duplicate /dial-patient POST (~20ms apart per ops report),
    // which then races with the /end that follows and 409s.
    if (joinedTranscriptRef.current === transcriptId) return;
    joinedTranscriptRef.current = transcriptId;
    let room;
    try {
      room = new Room({
        adaptiveStream: true,
        dynacast: true,
        /* LiveKit's default is to disconnect on `pagehide`. Chromium
           fires pagehide when the browser's beforeunload confirmation
           dialog appears — BEFORE the user has picked Reload or Cancel
           — so clicking Cancel would still leave the room torn down,
           silently killing an in-progress call the CM just chose to
           keep. Disable the auto-disconnect and rely on our own
           teardown paths (endCall / cancelCall / modal unmount) plus
           the LK server's WebRTC timeout for genuinely-abandoned tabs. */
        disconnectOnPageLeave: false,
      });
      // Auto-attach any remote audio track (patient's SIP audio) to a
      // hidden <audio> element so the CM hears it. Detach on unsubscribe
      // so we don't leak <audio> nodes across reconnects.
      room.on(RoomEvent.TrackSubscribed, (track, _pub, participant) => {
        if (!track || track.kind !== "audio") return;
        const el = track.attach();
        el.autoplay = true;
        el.playsInline = true;
        // Off-screen; the browser plays it regardless of position.
        el.style.display = "none";
        document.body.appendChild(el);
        const key = `${participant && participant.identity}:${track.sid || ""}`;
        remoteAudiosRef.current.set(key, el);
      });
      room.on(RoomEvent.TrackUnsubscribed, (track) => {
        if (!track) return;
        try { track.detach().forEach((el) => el.remove()); } catch (_) { /* noop */ }
      });
      // Live captions (interim + final text) published by the agent on the
      // lossy "caption" topic. Latest text per segId wins.
      room.on(RoomEvent.DataReceived, (payload, _participant, _kind, topic) => {
        if (topic !== "caption") return;
        let msg;
        try {
          msg = JSON.parse(new TextDecoder().decode(payload));
        } catch (_) {
          return;
        }
        if (!msg || msg.type !== "caption" || !msg.segId || typeof msg.text !== "string") return;
        setCaptions((prev) => ({
          ...prev,
          [msg.segId]: {
            id: msg.segId,
            speaker: msg.speaker,
            text: msg.text,
            final: !!msg.final,
            at: Date.now(),
          },
        }));
      });
      await room.connect(livekit.url, livekit.token);
      roomRef.current = room;

      // Ask for mic + publish. If permission is denied here, the catch
      // below tears everything down and surfaces the error.
      const mic = await createLocalAudioTrack();
      localMicRef.current = mic;
      await room.localParticipant.publishTrack(mic);
    } catch (err) {
      disconnectRoom();
      const msg = err && err.name === "NotAllowedError"
        ? "Mic access denied — call cancelled."
        : (err && err.message) || "Could not join the call.";
      setInitError(msg);
      // Tell the server the call is over so waiting_cm doesn't linger.
      if (pid && transcriptId) {
        transcriptService.end(pid, transcriptId, { expectedVersion: 0 },
          () => {}, () => {});
      }
      setStep(0);
      setSession(null);
      sessionRef.current = null;
      setCallState("");
      return;
    }

    // Room is live and mic is publishing — safe to dial the patient.
    transcriptService.dialPatient(pid, transcriptId,
      () => { /* poll will flip callState to ringing_patient */ },
      (err) => {
        setInitError((err && err.message) || "Could not dial the patient.");
      },
    );
  };
  const endSession = async () => {
    await pauseSession();
    setSession(null);
    sessionRef.current = null;
    setCallState("");
  };

  /* Unmount cleanup only. The `!open` effect that used to end here was
     the reason closing the dialog ended the call — under
     ActiveCallProvider the modal stays mounted across route changes and
     minimise, so hide-alone must NOT tear the room down. Unmount still
     ends: provider drops activeCall (S0/S1 X, S5 Done, hard dismiss) →
     modal unmounts → endSession fires. Idempotent — safe if endCall or
     cancelCall already ran the same teardown. */
  useEffect(() => () => { endSession(); /* eslint-disable-line */ }, []);

  // Care manager clicks Call patient — this is the full S0→S1 transition.
  // POST /initiate tells Go to:
  //   1. lookup CM's phone from employee profile (via auth token)
  //   2. create the LK room
  //   3. SIP-dial the CM via LK-SIP → wait for pickup
  //   4. SIP-dial the patient's number
  //   5. respond {transcriptId, callSid}
  // Both parties are on real phones. The browser is a pure HTTP client:
  // no LK connect, no mic permission, no share URL. Ringing / connected /
  // dropped state comes back through the transcript poll's callState
  // field and drives the step-transition useEffect above.
  const startCall = async (override) => {
    if (!patientId) {
      setInitError("Patient details missing — reopen the modal from a patient page.");
      return;
    }
    // Override, when passed from the address-book dialog, targets a
    // specific patient number (calling / primary / alternate) instead
    // of the default primary. Falls back to the patient's primary.
    // Care-manager phone is NEVER passed from the browser — Go looks
    // it up from the authenticated employee's profile.
    const cc = override && override.countryCode != null
      ? override.countryCode
      : person.countryCode;
    const ph = override && override.phoneNo != null
      ? override.phoneNo
      : person.phoneno;

    // Preferred path — delegate to the parent (useCallFlow), which
    // fires POST /api/v1/patient-communication/initiate-call, does
    // the mapped-caller-number lookup, and either seeds a fresh Aspire
    // session onto this modal (via the seededSession prop below) or
    // routes the call to SparkTG and closes this modal (via
    // onClose). This is the ONE call path phr-frontend uses now.
    // Legacy direct-initiate path below is kept only for callers that
    // don't wire onCallRequest (defensive; no in-tree caller).
    if (typeof onCallRequest === "function") {
      setInitError("");
      setInitiating(true);
      // Pass an onError hook so the parent can reset this modal's
      // "Connecting…" button state when /initiate-call fails — without
      // it the button stays stuck on "Connecting…" after the parent's
      // Call failed dialog is dismissed. Success path still clears
      // initiating via the seed useEffect once the aspire payload lands.
      onCallRequest(
        { countryCode: cc, phoneNo: ph },
        { onError: () => setInitiating(false) },
      );
      return;
    }

    // ── Legacy direct-initiate fallback ──
    setInitError("");
    setInitiating(true);
    transcriptService.initiate(
      patientId,
      {
        // Platform patient prop carries these as numbers; JSON encodes as
        // bare numerics which the Go string-typed bean rejects with 422.
        // Force string here so the API contract stays clean.
        patientCountryCode: cc != null ? String(cc) : "",
        patientPhoneNo:     ph != null ? String(ph) : "",
        // Scope the resulting Transcript to the currently-selected
        // appointment so the S0 rail filter (list?appointmentId=…)
        // picks it up on the next open. Omitted when the calling
        // screen didn't pick an appointment — same shape the primary
        // useCallFlow path already sends via /initiate-call.
        appointmentId: appointment && appointment.id
          ? Number(appointment.id) : undefined,
      },
      (resp) => {
        setSession({
          transcriptId: resp.transcriptId,
          callSid:      resp.callSid,
          version:      1,
        });
        setCallStartedAt(new Date().toISOString());
        setCallEndedAt("");
        setActiveTranscriptStatus("in-progress");
        setSavedCounts({});
        // Browser-mode when the initiate response carries a LiveKit
        // session; legacy SIP-mode otherwise. Seed callState so the S1
        // rail reads the right label immediately, before the first
        // poll tick lands ~2s later.
        const isBrowserMode = !!(resp && resp.livekitUrl && resp.livekitToken);
        setCallState(isBrowserMode ? "waiting_cm" : "ringing_cm");
        setStep(1);
        setInitiating(false);
        if (isBrowserMode) {
          joinRoomAndDial({
            patientId,
            transcriptId: resp.transcriptId,
            livekit: {
              url:   resp.livekitUrl,
              room:  resp.livekitRoom,
              token: resp.livekitToken,
            },
          });
        }
      },
      (err) => {
        setInitError((err && err.message) || "Could not start the call.");
        setInitiating(false);
      },
    );
  };

  const cancelCall = async () => {
    await endSession();
    setStep(0);
    // Re-read the S0 list so the just-cancelled transcript shows as
    // PENDING instead of a stale LIVE. Same pattern as the auto-drop
    // path (server flips in-progress → pending on /end; refetch has to
    // wait until that round-trip resolves).
    refetchTranscripts();
  };

  // Auto-start a call to a specific number when the modal is opened
  // with a staged target — the address-book flow from the patient page
  // uses this so a Call click over there flows straight into the
  // waiting-for-patient state without an extra "now click Call patient"
  // step. Guards on step===0 (fresh open, no live call) and !initiating
  // (protect against double-fire during retries). onAutoCallConsumed
  // nulls the caller's staged target so a re-open without a fresh pick
  // doesn't redial.
  const autoTargetKey = autoCallTarget
    ? `${autoCallTarget.countryCode}|${autoCallTarget.phoneNo}`
    : "";
  useEffect(() => {
    if (!open || !autoCallTarget || step !== 0 || initiating) return;
    // Seeded-session opens skip this branch — the session below wins.
    if (seededSession && seededSession.transcriptId) return;
    startCall(autoCallTarget);
    onAutoCallConsumed();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [open, autoTargetKey]);

  // Seeded session — when a transcript ID is handed in from the caller
  // (patient-communication's /initiate-call did it for us), skip our
  // own /initiate and land straight in the ringing state. The poll
  // effect keyed on `session.transcriptId` (defined below) picks up
  // from here and drives step transitions the same way it does after
  // a fresh /initiate. `seededSession.callingFrom` is a display-only
  // caption ("+91 XXXXX X3285") the header can surface — no wire
  // meaning.
  //
  // Fires on:
  //   • First-open with a pre-provisioned session (modal opens direct
  //     into ringing).
  //   • Subsequent Call-patient clicks inside the modal — the parent
  //     (useCallFlow) fires /initiate-call again, updates the prop
  //     with a NEW transcriptId, and this effect swaps the session.
  // Skips when the current in-modal session already matches the seed
  // (same call still in progress — don't restart mid-call).
  const seededTranscriptId = (seededSession && seededSession.transcriptId) || "";
  useEffect(() => {
    if (!open || !seededTranscriptId) return;
    if (session && session.transcriptId === seededTranscriptId) return;
    setSession({
      transcriptId: seededTranscriptId,
      callSid:      (seededSession && seededSession.callSid) || "",
      version:      1,
    });
    setCallStartedAt(new Date().toISOString());
    setCallEndedAt("");
    setActiveTranscriptStatus("in-progress");
    setSavedCounts({});
    const isBrowserMode = !!(seededSession && seededSession.livekitUrl && seededSession.livekitToken);
    setCallState(isBrowserMode ? "waiting_cm" : "ringing_cm");
    setStep(1);
    setInitiating(false);
    if (isBrowserMode) {
      joinRoomAndDial({
        patientId,
        transcriptId: seededTranscriptId,
        livekit: {
          url:   seededSession.livekitUrl,
          room:  seededSession.livekitRoom,
          token: seededSession.livekitToken,
        },
      });
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [open, seededTranscriptId]);

  // Care manager clicks End call in the header during a live call.
  // POST /end tells Go to hang up whichever SIP legs are still up + close
  // the LK room. Session context is kept for the review flow (poll needs
  // transcriptId + patientId to keep fetching captured items).
  const endCall = async () => {
    // ZY-10312 — optimistic flip of the active row's badge to PENDING
    // so S0 list reads correctly if the reviewer back-navigates before
    // the refetch resolves. pauseSession is already awaited (it posts
    // /end); refetch happens after so the list state matches the
    // server's post-end snapshot (not the pre-end LIVE one).
    flipActiveTranscriptToPending();
    await pauseSession();
    setCallEndedAt(new Date().toISOString());
    setStep(3);
    refetchTranscripts();
  };

  /* Expose endCall / cancelCall to ActiveCallProvider so the floater's
     hangup can drive the same internal teardown the header buttons use.
     Registered ONCE (deps []) with a stable forwarder pair that reads
     the latest closures from a ref — endCall and cancelCall are
     recreated every render (they capture session/state), so a naive
     "register on every render" would either race with a hangup click or
     churn the ref for no reason. */
  const controlsRef = useRef({ endCall: null, cancelCall: null });
  controlsRef.current.endCall = endCall;
  controlsRef.current.cancelCall = cancelCall;
  const registerHandleRef = useRef(registerHandle);
  registerHandleRef.current = registerHandle;
  useEffect(() => {
    if (!registerHandleRef.current) return undefined;
    registerHandleRef.current({
      endCall:    () => controlsRef.current.endCall && controlsRef.current.endCall(),
      cancelCall: () => controlsRef.current.cancelCall && controlsRef.current.cancelCall(),
    });
    return () => {
      if (registerHandleRef.current) registerHandleRef.current(null);
    };
  }, []);

  // Lock the page behind the modal (matches Dialog.js). Escape + backdrop
  // click are DELIBERATELY NOT wired to dismiss — a live call in progress,
  // an in-flight Save & next, or a review with unsaved captured items are
  // easy to lose by accident. The only exits are the header Close button
  // and the S5 "Done" button, both of which route through onClose after
  // the caller's own confirmation gates.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    const prevRoot = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    // The page scrolls on <html> here (it carries overflow-y: auto, which
    // stops body's overflow from reaching the viewport), so locking <body>
    // alone left the patient page scrolling UNDER the modal whenever a wheel
    // landed on a part that doesn't scroll — the header, the tabs — or ran
    // past a column's end (reported 2026-09-21).
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
      document.documentElement.style.overflow = prevRoot;
    };
  }, [open]);

  // Hooks below MUST run on every render, even when the modal is
  // closed — React's Rules of Hooks reject hooks called after an
  // early return. Both memos are cheap when open=false because
  // capturedItems / step / activeTranscriptStatus barely change while
  // the modal is dismissed.
  // Past-transcript / read-only presentation. Fires ONLY when the
  // reviewer has already completed a Save review pass on this
  // transcript (server status="reviewed"). A `"pending"` transcript
  // — call ended, review NOT yet saved — must stay editable when
  // reopened: it's a resumable review, not a past view. Including
  // "pending" here caused a deadlock (cards fell into fieldset[disabled]
  // via CapturedItemCard readOnly ribbon → the reviewer couldn't Add or
  // Cancel to drain the open-card count → Review-call button stayed
  // disabled → no way forward).
  const isReviewView =
    step >= 3 && activeTranscriptStatus === "reviewed";
  // Drug schedule saves land directly through pharmacy-dose-schedule-api
  // (not through the /captured pipeline like CC/Dx), so they never
  // appear in `capturedItems`. Track a local "touched during this call"
  // flag by listening for DS_V2_REFRESH — that only fires on a
  // successful save, mirroring how CC/Dx's dot only appears when a
  // captured item actually lands. Sheet-open events (DS_V2_ADD /
  // DS_V2_UPDATE) intentionally aren't listened to: opening a card
  // and cancelling out shouldn't leave the section marked as touched.
  const [drugScheduleTouched, setDrugScheduleTouched] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const onTouch = () => setDrugScheduleTouched(true);
    EventService.addListener(DS_V2_REFRESH, onTouch);
    return () => {
      EventService.removeListener(DS_V2_REFRESH, onTouch);
    };
  }, [open]);
  /* Reset when the session identity itself changes — a fresh call
     starting (null → new tid) or ending (tid → null). Keyed on session
     alone, not `open`, so restoring from minimise doesn't clobber a
     touch the CM made mid-call. */
  useEffect(() => {
    setDrugScheduleTouched(false);
  }, [sessionTranscriptId]);
  const sectionsWithCaptures = useMemo(() => {
    const s = new Set();
    for (const it of capturedItems || []) {
      if (!it || !it.targetSection) continue;
      if (it.state === "discarded_in_call" || it.state === "discarded_in_review") continue;
      // Soft-deleted items don't count — otherwise a section with only
      // rejected DB-updates / blank-new-entries would still render as a
      // Review tab with nothing inside.
      if (it.deletedAt) continue;
      s.add(it.targetSection);
    }
    if (drugScheduleTouched) s.add("drug_schedule");
    return s;
  }, [capturedItems, drugScheduleTouched]);
  const visibleTabs = useMemo(
    () => (isReviewView
      ? SECTION_TABS.filter((t) => sectionsWithCaptures.has(TAB_TO_SECTION[t.id]))
      : SECTION_TABS),
    [isReviewView, sectionsWithCaptures],
  );

  // Reopening a reviewed transcript — if the sticky `activeSection`
  // from a prior view ("patient-details" by default, or whatever the
  // reviewer last clicked) isn't among the sections THIS transcript
  // captured, the left pane shows an unhelpful "Nothing captured"
  // instead of the actual review. Snap to the first visible tab so
  // the first section with captures is pre-selected on open.
  useEffect(() => {
    if (!isReviewView) return;
    if (!visibleTabs || visibleTabs.length === 0) return;
    if (visibleTabs.some((t) => t.id === activeSection)) return;
    setActiveSection(visibleTabs[0].id);
  }, [isReviewView, visibleTabs, activeSection]);

  // ZY-10356 — the modal stays mounted even when minimised (so a live
  // call's LiveKit room survives), but the tree inside was previously
  // unmounted via `if (!open) return null`. That dropped every open
  // card's form field state the moment the reviewer hit X to
  // minimise. Keep the overlay rendered and let CSS hide it; the
  // `is-hidden` modifier switches display:none + pointer-events:none
  // so the floater beneath stays interactive and nothing takes focus.
  const hidden = !open;

  const name = person.name || PATIENT_FALLBACK.name;
  const phone = person.phone || PATIENT_FALLBACK.phone;
  const pid = person.pid || PATIENT_FALLBACK.pid;
  const railPatient = { name, phone, pid };

  const stateKey = `s${Math.min(step, 4)}`;
  // Header subtitle stays fixed on patient identity across every step —
  // the transient state message (calling, in progress, ended) is carried
  // by the status pill on the header's right side.
  const subtitle = `${name} · ${phone}`;
  const pill = PILLS[step];
  // Care manager's display name for the "Quick reminder" script card on
  // the calling screen. "Dr." prefix belongs to CATEGORY 1 (Doctor)
  // only — nutritionists, physiotherapists, psychotherapists and the
  // medical team introduce themselves without one. UserService.isDoctor()
  // gates the prefix so a nutritionist named "Nilesh" doesn't render as
  // "Dr. Nilesh". Names already carrying a title (Dr / Mr / Ms / Mrs)
  // pass through unchanged in every case.
  const careManagerName = (() => {
    const raw = (new AuthenticationService().getUserName() || "").trim();
    if (!raw || raw === "User") return "Care manager";
    if (/^(dr\.?|mr\.?|ms\.?|mrs\.?)\b/i.test(raw)) return raw;
    return new UserService().isDoctor() ? `Dr. ${raw}` : raw;
  })();
  // Header CTA state:
  //   step 0                → Call patient button (enabled)
  //   step 1 (ringing)      → Cancel call button (danger)
  //   step 2 (in-call)      → End call button (danger)
  //   step 3 (call ended)   → Review call button (enabled if items exist)
  //   step 4/5/6            → no header CTA (dialogs / review own the top)
  const inReviewOverlay  = step >= 4;
  const inCall           = step === 1 || step === 2;
  // ZY-10356 — the back arrow lets the reviewer leave the live-call
  // view for the transcript list while the call keeps running. In that
  // state `step` is 0 (list view) but a LiveKit room is still up, so
  // step-based checks under-count call activity. `isCallLive` reads
  // the actual call lifecycle flag (callState) — any non-idle, non-
  // terminal value counts. Downstream: Call patient / Address book
  // disabled, X close minimises instead of ending.
  const isCallLive       = !!callState && callState !== "dropped";
  const canStartCall     = step === 0 && !initiating && !isCallLive;
  // Once the call has ended (step === 3) the header offers Review call
  // — unless the transcript is already REVIEWED (reopening a past
  // reviewed one from the S0 list). If nothing was captured on an
  // unreviewed transcript, the summary dialog shows an empty state.
  const showReviewCall   = step === 3 && activeTranscriptStatus !== "reviewed";
  // Review call is enabled only when every section's open-card count is
  // 0. Any AI proposal awaiting a decision — or a manually-opened editor
  // sitting on-screen — blocks review. Cancel counts as resolving a
  // card, so this always eventually reaches 0 without forcing the user
  // to accept anything they didn't want.
  const allCardsResolved = Object.values(openCountsBySection).every((n) => !n);
  const reviewCallDisabled = !allCardsResolved;
  const reviewCallTitle    = reviewCallDisabled
    ? "Resolve every open card in every section before reviewing"
    : "Review captured items";
  const showEndCall      = step === 2;
  // Ringing state — before the call connects — surfaces a Cancel call
  // header CTA in place of the disabled Call-patient button. `initiating`
  // masks the very brief /initiate window with the "Connecting…" label
  // so we don't flash Cancel before there's actually anything to cancel.
  const showCancelCall   = step === 1 && !initiating;
  // Address book affordance is a "who to call next" list — only makes
  // sense on step 0 (the transcript-list rail). Once the user has
  // started or reopened a transcript, hide it so it can't compete
  // with the call / review flow.
  const showAddressBook  = step === 0;
  const showLegacyRight  = false;

  // Past-transcript view: the reopened transcript's status is `pending`
  // or `reviewed` (fresh calls have "" until they end). When true we
  // (a) swap the middle-column render for the S5-style captured-items
  // table (see CapturedSections viewMode="review"), and (b) filter
  // the tab strip to sections THIS transcript actually touched so the
  // reviewer isn't wading through 12 tabs to find the 2 that matter.
  // Live calls keep the full 12 tabs so newly-captured items appear
  // immediately when the AI proposes them. `isReviewView` +
  // `visibleTabs` are hoisted ABOVE the `if (!open) return null` guard
  // (see the useMemo block near the top of the render) so their hooks
  // run on every render — React's Rules of Hooks reject hooks called
  // after an early return.

  return ReactDOM.createPortal(
    // ZY-10356 Phase 1 — CardDraftProvider lives at the MODAL root
    // (not inside CapturedSections) so draft snapshots survive step
    // transitions. Back-nav from Live call → List (step 2 → 0)
    // unmounts CapturedSections but keeps this provider mounted,
    // preserving the Map ref across tab/step changes. Only a full
    // modal dismiss (activeCall → null) unmounts the provider.
    <CardDraftProvider>
    <div
      className={`ait-overlay${hidden ? " is-hidden" : ""}`}
      aria-hidden={hidden}
      /* Backdrop click intentionally does NOT dismiss the modal —
         losing an in-progress call, a mid-save review, or unsaved
         captured items to a stray click was too easy. Users exit via
         the header Close button and the S5 Done button only. */
    >
      {step === 4 ? (
        <ReviewSummary
          items={capturedItems}
          startedAt={callStartedAt}
          onClose={() => setStep(3)}
          onStartReview={() => setStep(5)}
          onFinishEmpty={() => {
            // Nothing was captured — flip the transcript to REVIEWED
            // on the backend by calling SaveSection with any section
            // (the service marks status=reviewed once every captured
            // item is terminal; an empty captured list is vacuously
            // terminal). Any section id works; we use chief_complaints
            // as the canonical first tab. Then close the whole modal —
            // there's nothing more for the reviewer to look at.
            saveSection("chief_complaints").catch(() => {
              /* silent — the reviewer's next open of this transcript
                 will still see PENDING; server hasn't been reached. */
            });
            setActiveTranscriptStatus("reviewed");
            onClose();
          }}
        />
      ) : step === 5 ? (
        <ReviewSave
          items={capturedItems}
          turns={liveTurns}
          startedAt={callStartedAt}
          endedAt={callEndedAt}
          onClose={() => setStep(3)}
          onFinish={() => setStep(6)}
          onSaveSection={saveSection}
          patient={person}
          patientId={patientId}
          appointmentId={appointment && appointment.id ? Number(appointment.id) : 0}
          transcriptId={session ? session.transcriptId : ""}
          transcriptService={transcriptService}
          version={session ? session.version || 0 : 0}
          // Pass the authoritative ref too so CapturedSections can read
          // the latest version synchronously at mutation time — the
          // `version` prop above lags one render whenever setSession
          // hasn't flushed, which was the root of ZY-10356 409s on
          // Reject / Add immediately after a background poll bump.
          versionRef={latestVersionRef}
          onItemsSync={setCapturedItems}
          onVersionSync={(v) => {
            // Bump the ref BEFORE the state update so any concurrent
            // stale poll callback fails its version check.
            if (v > latestVersionRef.current) latestVersionRef.current = v;
            setSession((prev) => (prev ? { ...prev, version: v } : prev));
          }}
          reviewedSections={reviewedSections}
          onEditSection={(section) => {
            // ZY-10356 Phase 4 — Edit from Review drops the reviewer
            // back to Call mode (step 3) with the chosen section active.
            // ReviewSave emits the Go section slug ("chief_complaints");
            // activeSection state here is the kebab-case tab id
            // ("chief-complaints"), so map through SECTION_TO_TAB. Fall
            // back to the raw string if the map misses so the step
            // transition at least happens.
            const tabId = SECTION_TO_TAB[section] || section;
            setActiveSection(tabId);
            setStep(3);
          }}
        />
      ) : step === 6 ? (
        <ReviewDone
          counts={savedCounts}
          onDone={onClose}
        />
      ) : (
        <div
          className="ait-modal"
          role="dialog"
          aria-modal="true"
          aria-label="ECHO - AI transcript"
        >
          <header className="ait-head">
            <span className="ait-icon-tile" aria-hidden="true">
              <EchoSparkle className="ait-icon-spark" />
            </span>
            <div className="ait-head-titles">
              <h2 className="ait-title">ECHO - AI transcript</h2>
              <p className="ait-subtitle">{subtitle}</p>
              {/* "Calling from: <mapped-number>" used to sit here to tell
                  the CM which SparkTG-routed caller ID would show on the
                  patient's phone. Under browser-mode (ZY-10202) the CM's
                  leg is LiveKit WebRTC — no CM phone rings, no caller ID
                  is meaningful — so the row is dropped. */}
            </div>
            {pill ? <span className={`ait-pill ${pill.cls}`}>{pill.label}</span> : null}
            {/* Header CTA swaps based on step. Only ONE of these renders
                at a time. Address-book icon stays visible for steps 0-2
                but is disabled while a call is live. Steps 4-6 hide both
                because the summary / review / done overlays own the top.
                Step 2 shows End call (red); step 3 shows Review call
                (indigo); otherwise Call patient (orange). */}
            {showEndCall ? (
              <button
                type="button"
                className="ait-head-cta ait-head-cta-danger"
                onClick={endCall}
                title="End the call for you (patient stays on their side until they leave)"
              >End call</button>
            ) : showCancelCall ? (
              <button
                type="button"
                className="ait-head-cta ait-head-cta-danger"
                onClick={cancelCall}
                title="Cancel this call attempt — nothing will be recorded"
              >Cancel call</button>
            ) : showReviewCall ? (
              <button
                type="button"
                className="ait-head-cta ait-head-cta-review"
                /* Skip the step-4 ReviewSummary dialog per product
                   decision — land directly on step 5 (Review & save).
                   The step===4 render block below is kept dormant so
                   flipping back is a one-line change. If every captured
                   item is rejected / deleted (nothing to review),
                   surface a small OK/Cancel prompt instead of landing
                   on an empty Review-save screen. */
                onClick={() => {
                  const reviewable = (capturedItems || []).filter(
                    (it) => it
                      && it.targetSection
                      && it.state !== "rejected"
                      && !it.deletedAt,
                  ).length;
                  if (reviewable === 0) {
                    setNothingToReviewOpen(true);
                    return;
                  }
                  setStep(5);
                }}
                disabled={reviewCallDisabled}
                title={reviewCallTitle}
              >Review call</button>
            ) : !inReviewOverlay ? (
              <button
                type="button"
                className="ait-head-cta"
                onClick={startCall}
                disabled={!canStartCall}
                title={
                  canStartCall ? "Call patient"
                  : isCallLive  ? "A call is in progress"
                  : "Not available right now"
                }
              >
                {initiating ? "Connecting…" : "Call patient"}
              </button>
            ) : null}
            {showAddressBook ? (
            <button
              type="button"
              className="ait-head-iconbtn"
              aria-label="Phonebook"
              title={isCallLive ? "A call is in progress" : "Phonebook"}
              onClick={() => setAddressBookOpen(true)}
              disabled={isCallLive}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
                stroke="currentColor" strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 4h11a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6z" />
                <circle cx="12" cy="11" r="2.4" />
                <path d="M8.4 16.5c.6-1.4 2-2.3 3.6-2.3s3 .9 3.6 2.3" />
                <path d="M3 7.5h3M3 11.75h3M3 16h3" />
              </svg>
            </button>
            ) : null}
            <button type="button" className="ait-close" aria-label="Close" onClick={onClose}>
              &times;
            </button>
          </header>

          {/* Section-tab strip — hoisted OUT of CapturedSections so it
              spans the full modal width (over the rail + middle
              columns), matching the design mock. `.ait-tabs-row`
              draws the horizontal separator between the strip and the
              grid below.

              Leading back button (ZY-10356) takes the reviewer from
              the live / ended-call view back to the transcript List
              view. The call lifecycle is independent of this nav — a
              live call stays live and the floater takes over if the
              list view is active; an ended call is just left in its
              ended state and can be reopened from the list. The X
              close in the header still closes the whole modal. */}
          {step > 1 && (
            <div className="ait-tabs-row">
              <button
                type="button"
                className="ait-tabs-back"
                aria-label="Back to transcript list"
                onClick={() => setStep(0)}
                title="Back to transcript list"
              >
                <svg viewBox="0 0 20 20" width="16" height="16" fill="none"
                     stroke="currentColor" strokeWidth="1.8"
                     strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 4l-6 6 6 6" />
                </svg>
              </button>
              <span className="ait-tabs-back-sep" aria-hidden="true" />
              <SectionTabs
                tabs={visibleTabs}
                activeTab={activeSection}
                onSelect={setActiveSection}
                openCounts={openCountsBySection}
                modifiedSections={sectionsWithCaptures}
                reviewedSections={reviewedSections}
              />
            </div>
          )}

          <div className={`ait-body${showLegacyRight ? " has-right" : ""}`}>
            <LiveCallPanel
              state={stateKey}
              patient={railPatient}
              transcripts={transcripts}
              loading={loadingList}
              error={loadError}
              initError={initError}
              callState={callState}
              liveTurns={railTurns}
              capturedItems={capturedItems}
              callStartedAt={callStartedAt}
              callEndedAt={callEndedAt}
              onPlayRecording={handlePlayRecording}
              onSelectTranscript={(t) => {
                // Seed the modal's own started/ended state from the
                // selected transcript so the rail's LIVE / ENDED
                // duration counter shows the real call length. Without
                // this the rail defaults to 0:00 (both are empty
                // strings on fresh mount). Status seeds the header
                // gates (hide Review call once reviewed, hide address
                // book once we're inside an existing transcript).
                if (t) {
                  setCallStartedAt(t.startedAt || "");
                  setCallEndedAt(t.endedAt || "");
                  setActiveTranscriptStatus(t.status || "");
                  // Seed a minimal session so downstream code paths
                  // that need transcriptId + version (saveSection,
                  // decideCaptured, propose, consent) can address the
                  // reopened transcript. Version is fetched fresh via
                  // GET so the first mutating write doesn't 409 on a
                  // stale expectedVersion. `room` / `cleanup` etc.
                  // stay nil — they were only relevant while the
                  // LiveKit call was active.
                  transcriptService.get(
                    patientId,
                    t.id,
                    (full) => {
                      const v = (full && full.version) || 0;
                      latestVersionRef.current = v;
                      setSession({
                        transcriptId: t.id,
                        version:      v,
                      });
                      if (Array.isArray(full && full.captured)) {
                        setCapturedItems(full.captured);
                      }
                    },
                    () => {
                      // Fall back to version 0 — the first write will
                      // 409 and reload; better than leaving session
                      // null and having every callback throw.
                      setSession({ transcriptId: t.id, version: 0 });
                    },
                  );
                }
                // in-progress → live view (S2). Ended → post-call
                // review-start state (S3) so the header shows Review call.
                // ZY-10312 — require BOTH `status === "in-progress"` AND
                // the absence of an endedAt stamp before opening live
                // view. A row with endedAt set is a dead session regardless
                // of a stale status flag — opening S2 for it would land the
                // reviewer on a red "End call" button for a call that
                // can't end again. Fallback is the review view (S3).
                if (t && t.status === "in-progress" && !t.endedAt) setStep(2);
                else setStep(3);
              }}
              onCancelCall={cancelCall}
              onSaveAndNext={() => {}}
            />

            <div className="ait-middle">
              {step === 2 && session && consentStatus !== "captured" ? (
                <ConsentBanner
                  status={consentStatus}
                  error={consentError}
                  onCapture={() => postConsent(true)}
                  onDecline={() => postConsent(false)}
                />
              ) : null}
              {/* ZY-10356 Phase 1 — once CapturedSections has mounted
                  (step reached 2+), keep it mounted via a CSS hide
                  swap so its per-section open-card arrays (and the
                  forms inside) survive back-nav to the list view
                  (step 0). Without this, a reviewer back-navigating
                  from Live call → List and returning loses every open
                  card because CapturedSections unmounts on the ternary
                  flip. QuickReminder stays conditional — it's purely
                  pre-call copy with no state to preserve. */}
              {step <= 1 && (
                <div className="ait-quick-reminder">
                  <div className="ait-qr-icon" aria-hidden="true">
                    {/* Person + speech waves — a "talk to the patient"
                        cue for the consent script below. Inline SVG so
                        no icon-font dependency; stroke uses currentColor
                        so the maroon fill/foreground pair drives both. */}
                    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="10" cy="8" r="3.2" />
                      <path d="M4 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
                      <path d="M17.2 6.6c1.1 1 1.8 2.4 1.8 4s-.7 3-1.8 4" />
                      <path d="M20 4c1.9 1.7 3 4.2 3 6.9s-1.1 5.2-3 6.9" />
                    </svg>
                  </div>
                  <p className="ait-qr-eyebrow">Quick reminder</p>
                  <h3 className="ait-qr-title">Take consent before you begin</h3>
                  <p className="ait-qr-script">
                    &ldquo;Hi, I&rsquo;m <strong>{careManagerName}</strong> from Zyla Health.
                    This call is recorded for quality purposes, and your
                    information stays private.&rdquo;
                  </p>
                  <div className="ait-qr-divider" aria-hidden="true" />
                  <p className="ait-qr-footnote">
                    Sections are ready · AI will capture complaints, diagnoses,
                    medicines and more during the call.
                  </p>
                </div>
              )}
              {capturedEverMounted && (
                <div style={{ display: step > 1 ? undefined : "none" }}>
                  {/* ZY-10356 Phase 1 — key the instance on
                      transcriptId so switching transcripts (e.g.
                      clicking a past transcript in the list while a
                      live call is up) remounts a fresh CapturedSections
                      with empty open-card arrays. Without the key, the
                      live call's open cards would bleed into the past
                      transcript's view since the same instance is now
                      kept mounted across step transitions. Within one
                      transcript the key is stable, so the state still
                      survives back-nav + minimize. */}
                  <CapturedSections
                    key={(session && session.transcriptId) || "no-session"}
                    openCardsStoreRef={openCardsStoreRef}
                    tabs={visibleTabs}
                    activeTab={activeSection}
                    onSelectTab={setActiveSection}
                    hideTabs
                    /* Past-transcript view — see isReviewView above:
                       switches the middle column to the S5-style
                       captured-items table. Fresh calls fall through
                       to the standalone-embed view as before. */
                    viewMode={isReviewView ? "review" : ""}
                    items={capturedItems}
                    patient={person}
                    awaitingConsent={step === 2 && consentStatus !== "captured"}
                    callEnded={step >= 3}
                    patientId={patientId}
                    appointmentId={appointment && appointment.id ? Number(appointment.id) : 0}
                    transcriptId={session ? session.transcriptId : ""}
                    transcriptService={transcriptService}
                    version={session ? session.version || 0 : 0}
                    onItemsSync={setCapturedItems}
                    onVersionSync={(v) => {
                      if (v > latestVersionRef.current) latestVersionRef.current = v;
                      setSession((prev) => (prev ? { ...prev, version: v } : prev));
                    }}
                    onOpenCountsChange={setOpenCountsBySection}
                    reviewedSections={reviewedSections}
                    onReviewedSectionsSync={setReviewedSections}
                  />
                </div>
              )}
            </div>

          </div>

          {/* Floating recording player. Anchored to the modal shell so
              it hovers over the S0 list without shifting layout. Hidden
              when player is null. Rendered inside the modal (not
              portalled) so it disappears when the modal is closed. */}
          {(playerLoading || player || playerError) && (
            <div className="ait-recording-bar" role="dialog" aria-label="Recording playback">
              {playerLoading ? (
                <span className="ait-recording-bar-label">Loading recording…</span>
              ) : playerError ? (
                <>
                  <span className="ait-recording-bar-label ait-recording-bar-err">{playerError}</span>
                  <button type="button" className="ait-recording-bar-close" onClick={closePlayer} aria-label="Close">×</button>
                </>
              ) : (
                <>
                  <span className="ait-recording-bar-label">Recording · {player.label}</span>
                  <audio
                    controls
                    autoPlay
                    src={player.url}
                    className="ait-recording-bar-audio"
                    preload="metadata"
                  >
                    <source src={player.url} type={player.contentType} />
                  </audio>
                  <button type="button" className="ait-recording-bar-close" onClick={closePlayer} aria-label="Close">×</button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Address book — reachable from the header icon (steps 0-3, and the
          gate hides it during a live call). Every Call button in the dialog
          closes it then calls startCall(override), so the standard S1
          waiting-for-patient flow kicks in with the picked number. Never
          rendered while step >= 1 so an accidental Call inside the dialog
          during an active call is impossible even if the icon somehow
          leaked back into view. */}
      {step === 0 ? (
        <AddressBookDialog
          open={addressBookOpen}
          patient={person}
          appointment={appointment}
          onClose={() => setAddressBookOpen(false)}
          onCall={({ countryCode, phoneNo }) => {
            setAddressBookOpen(false);
            startCall({ countryCode, phoneNo });
          }}
          disableCallButtons={initiating || step !== 0}
        />
      ) : null}
      {nothingToReviewOpen ? (
        <ModalPortal>
          <div
            className="cc-modal-backdrop rejc-backdrop"
            onMouseDown={() => setNothingToReviewOpen(false)}
          >
            <div
              className="rejc"
              onMouseDown={(e) => e.stopPropagation()}
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="ait-nothing-to-review-title"
            >
              <h2 id="ait-nothing-to-review-title" className="rejc-title">
                Nothing to review
              </h2>
              <p className="rejc-body">
                This transcript doesn&apos;t have any captured items to review.
                Mark it as reviewed and close, or go back to revisit a section.
              </p>
              <footer className="rejc-actions">
                <button
                  type="button"
                  className="rejc-cancel"
                  onClick={() => setNothingToReviewOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rejc-reject"
                  onClick={() => {
                    // Empty-is-vacuously-terminal: SaveSection on any
                    // section flips server status to reviewed when every
                    // captured item is terminal (zero captured items
                    // qualify). Mirrors the old ReviewSummary onFinish-
                    // Empty behaviour.
                    saveSection("chief_complaints").catch(() => {
                      /* silent — next open shows PENDING if the server
                         wasn't reached. */
                    });
                    setActiveTranscriptStatus("reviewed");
                    setNothingToReviewOpen(false);
                    onClose();
                  }}
                >
                  OK
                </button>
              </footer>
            </div>
          </div>
        </ModalPortal>
      ) : null}
    </div>
    </CardDraftProvider>,
    document.body
  );
};

AiTranscriptModal.propTypes = {
  /** Nothing renders when false. */
  open: PropTypes.bool.isRequired,
  /** Called by the header Close button and the S5 "Done" button.
   *  Escape + backdrop click are DELIBERATELY NOT wired to dismiss —
   *  see the useEffect in the body for the rationale. */
  onClose: PropTypes.func.isRequired,
  /** Optional subtitle source; falls back to sample "Vineeta Verma · +91-9413XXXXXX". */
  patient: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    phone: PropTypes.string,
    pid: PropTypes.string,
  }),
  appointment: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    phone: PropTypes.string,
    pid: PropTypes.string,
  }),
  /**
   * Set by callers who want the modal to auto-place a call to a specific
   * number the moment it opens (address-book on the patient page uses
   * this). When present and step===0, we fire startCall(target) once and
   * then call onAutoCallConsumed to null it out so re-opening the modal
   * without a fresh target doesn't re-dial.
   */
  autoCallTarget: PropTypes.shape({
    countryCode: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    phoneNo:     PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  onAutoCallConsumed: PropTypes.func,
  /** (live: boolean) => void — whether a call is ringing or connected. */
  onCallActiveChange: PropTypes.func,
  /**
   * Optional pre-provisioned LiveKit session. When set, the modal
   * skips its own POST /initiate and lands straight in the ringing
   * state. Wired by useCallFlow after the unified
   * /api/v1/patient-communication/initiate-call endpoint returns
   * `vendor: "aspire"` with `transcriptId` already in hand.
   */
  seededSession: PropTypes.shape({
    transcriptId: PropTypes.string,
    callSid:      PropTypes.string,
    callingFrom:  PropTypes.string, // display-only "+91 XXXXX X3285"
  }),
  /**
   * Delegated call trigger. When provided, the modal's header CTA and
   * the address-book Call button hand the click UP to the parent
   * (useCallFlow) instead of firing POST /initiate directly. The
   * parent fires POST /initiate-call, routes to the right vendor, and
   * either swaps the modal's seededSession (Aspire) or closes it in
   * favour of the SparkTG confirm dialog. Called with `{countryCode,
   * phoneNo}` — the target number the modal wanted to dial.
   */
  onCallRequest: PropTypes.func,
  /* Provider bridges — see the destructure at the top of the component. */
  onSessionChange:    PropTypes.func,
  onCallStateChange:  PropTypes.func,
  onStepChange:       PropTypes.func,
  onInitErrorChange:  PropTypes.func,
  /** (handle: {endCall, cancelCall} | null) => void — called on mount
   *  with the modal's imperative controls so ActiveCallProvider can
   *  drive endCall from the floater. Called with null on unmount. */
  registerHandle:     PropTypes.func,
};

AiTranscriptModal.defaultProps = {
  patient: null, appointment: null,
  autoCallTarget: null, onAutoCallConsumed: () => {},
  onCallActiveChange: null,
  seededSession: null,
  onCallRequest: null,
  onSessionChange: null,
  onCallStateChange: null,
  onStepChange: null,
  onInitErrorChange: null,
  registerHandle: null,
};

export default AiTranscriptModal;
